import React, { useEffect, useMemo } from "react";
import { Linking } from "react-native";
import { StackScreenProps } from "@react-navigation/stack";
import { useQuery } from "@apollo/client/react";
import { ProfileStackParamList } from "../../navigations/ProfileStack";
import { useI18n } from "../../hooks/i18n-hooks";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { ClientProfile } from "../../__generated__/graphql";
import ScreenCard from "../ScreenCard";
import UserAvatar from "../user/UserAvatar";
import ListHeader from "../../ui_atoms/ListHeader";
import ListItem from "../../ui_atoms/ListItem";
import Text from "../../components/Text";
import Gap from "../../ui_atoms/Gap";

type Props = StackScreenProps<ProfileStackParamList, "ColleagueDetails">;

const ColleagueDetailsScreen: React.FC<Props> = ({ route, navigation }) => {
    const { colleagueId } = route.params;
    const currentProfile = useCurrentProfile<ClientProfile>();
    const i18n = useI18n();

    const { data: { getClientUsers: clientUsers = [] } = {} } = useQuery(GET_CLIENT_USERS, {
        variables: {
            clientId: currentProfile?.client?.id || "",
        },
    });

    const colleague = useMemo(
        () => clientUsers.find((user) => user.id === colleagueId),
        [clientUsers, colleagueId],
    );

    useEffect(() => {
        navigation.setOptions({ headerTitle: `${colleague?.firstName} ${colleague?.lastName}` });
    }, [colleague?.firstName, colleague?.lastName, navigation]);

    if (!colleague) {
        return (
            <ScreenCard>
                <Text>{i18n.t("not_found")}</Text>
            </ScreenCard>
        );
    }

    return (
        <ScreenCard>
            <Gap gap={1} />
            <UserAvatar
                avatarPath={colleague.avatarUrl}
                size={100}
                style={{ alignSelf: "center" }}
            />
            <Gap gap={1} />
            <ListHeader value={i18n.t("screens.user.colleague_details.colleague")} />
            <ListItem
                avatarPath={colleague.avatarUrl}
                description={`${colleague?.firstName} ${colleague?.lastName}`}
            />
            <ListItem
                description={i18n.t("screens.user.colleague_details.contracts")}
                icon="file"
                onPress={() => {
                    navigation.navigate("ColleagueContracts", { colleagueId });
                }}
                rightIcon="chevron-right"
                rightActionMode="navigate"
            />
            <ListItem
                description={colleague.phoneNumber || ""}
                icon="phone"
                onPress={() => {
                    if (colleague.phoneNumber) {
                        void Linking.openURL(`tel:${colleague.phoneNumber}`);
                    }
                }}
                rightIcon="phone"
                rightActionMode="action"
            />
            <ListItem
                description={colleague.email || ""}
                icon="email"
                onPress={() => {
                    if (colleague.email) {
                        void Linking.openURL(`mailto:${colleague.email}`);
                    }
                }}
                rightIcon="email"
                rightActionMode="action"
            />
        </ScreenCard>
    );
};

export default ColleagueDetailsScreen;
